/**
 * GraphNode — card with 3px left accent bar (research_ui_v2 handoff §6.1).
 *
 * Anatomy:
 *   ┌─────────────────────────────────┐
 *   │▎TYPE LABEL                      │   type label in node-type color
 *   │ Body label (up to 3 lines)      │   13px semibold text-fg
 *   │ meta · findings · (citation)    │   optional mono meta row
 *   └─────────────────────────────────┘
 *
 * State visuals:
 *   default  — bg-surface, subtle border, shadow-xs
 *   hover    — shadow-md, -translate-y-px
 *   selected — border in node-type color + 3px ring at 18% alpha
 *   open     — same as selected, plus a calm elevated shadow (persistent)
 *   dimmed   — opacity 0.3 when a highlight path is active and this node is off-path
 *   question — subtle question-blue tint gradient
 *   conclusion — subtle success-green tint gradient
 */

import React, { memo, useContext } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { NodeType, getNodeTypeColor, getNodeTypeLabel } from '../types';
import { OpenNodeIdContext } from '../EvidenceBoard';
import { HighlightContext } from '../../shared/HighlightContext';

// Fixed card height — single source of truth shared with the cluster layout
// (EvidenceBoard hands this same value to ELK as the node height). Mirrors the
// build view's compact ModelingNode (42px): content vertically centered so the
// card, its ELK slot, and its tier band always agree — no drift.
export const GRAPH_NODE_HEIGHT = 42;
// Fixed card width — also shared with the ELK layout (single source of truth),
// squished narrower than build's 184 to tighten the workspace horizontally.
export const GRAPH_NODE_WIDTH = 160;

export interface GraphNodeData {
  nodeType: NodeType;
  label: string;
  isSelected?: boolean;
  isOpen?: boolean;
  isRoot?: boolean;
  isTerminal?: boolean;
  childCount?: number;
  findingCount?: number;
  // Expand/collapse for group nodes
  isExpanded?: boolean;
  onToggleExpand?: (nodeId: string) => void;
  nodeId?: string;
  // Plan node fields (#89)
  workspaceType?: string;  // "research" | "production" | "institution_context"
  deliverable?: string;    // What this workspace produces (1-3 words)
}

const WORKSPACE_TYPE_LABELS: Record<string, string> = {
  research: 'Research',
  production: 'Build',
  institution_context: 'Context',
};

function GraphNode({ data, selected, id }: NodeProps<GraphNodeData>) {
  const {
    nodeType, label, isRoot, isTerminal, childCount,
    isExpanded, onToggleExpand, workspaceType,
  } = data;

  const color = getNodeTypeColor(nodeType);
  const typeLabel = (workspaceType && WORKSPACE_TYPE_LABELS[workspaceType])
    ? WORKSPACE_TYPE_LABELS[workspaceType]
    : getNodeTypeLabel(nodeType);
  const displayLabel = label || nodeType.replace(/_/g, ' ');

  // State
  const isSelected = selected || data.isSelected;
  const openNodeId = useContext(OpenNodeIdContext);
  const isOpen = id === openNodeId;
  const highlight = useContext(HighlightContext);
  const isDimmed = highlight.active && !highlight.nodeIds.has(id);

  const isGroup = nodeType === 'group';
  const hasChildren = childCount !== undefined && childCount > 0;
  const canExpand = isGroup && hasChildren;

  // Subtle tint for the two most prominent node types
  const tintBackground =
    nodeType === 'question'   ? `linear-gradient(180deg, ${color}0A 0%, var(--surface) 100%)` :
    nodeType === 'conclusion' ? `linear-gradient(180deg, ${color}10 0%, var(--surface) 100%)` :
    undefined;

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleExpand && id) onToggleExpand(id);
  };

  const cardStyle: React.CSSProperties = {
    // Fixed width + height (single source of truth shared with ELK).
    width: GRAPH_NODE_WIDTH,
    height: GRAPH_NODE_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    background: tintBackground ?? 'var(--surface)',
    borderColor: (isSelected || isOpen) ? color : 'var(--border-default)',
    boxShadow: isSelected
      ? `0 0 0 3px ${color}2E, var(--shadow-md)`
      : isOpen
      ? 'var(--shadow-md)'
      : 'var(--shadow-xs)',
  };

  return (
    <div
      className="relative group"
      style={{ opacity: isDimmed ? 0.3 : 1, transition: 'opacity 0.3s ease' }}
    >
      {/* Target handle (top) */}
      <Handle
        type="target"
        position={Position.Top}
        className={`!w-3 !h-3 !border-2 !border-surface !-top-1.5 ${isRoot ? '!bg-transparent !border-transparent' : ''}`}
        style={{ backgroundColor: isRoot ? undefined : 'var(--border-strong)' }}
      />

      {/* Card */}
      <div
        className="relative rounded-md border px-[11px] py-[6px] transition-all duration-200 hover:-translate-y-px overflow-hidden"
        style={cardStyle}
      >
        {/* 2px left accent bar */}
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-[2px]"
          style={{ backgroundColor: color }}
        />

        {/* Type label */}
        <div
          className="text-[9px] font-bold uppercase tracking-[0.06em] leading-none mb-0.5"
          style={{ color }}
        >
          {typeLabel}
        </div>

        {/* Body — single line with ellipsis (full text on hover), build-parity */}
        <div
          className="truncate text-[12px] leading-tight font-semibold text-fg"
          title={displayLabel}
        >
          {displayLabel}
        </div>
      </div>

      {/* Expand/collapse for group nodes with children */}
      {canExpand && (
        <button
          onClick={handleExpandClick}
          className="absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center border-2 bg-surface shadow-bark-sm hover:scale-110 transition-transform"
          style={{ borderColor: color, color }}
          title={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <ChevronDown size={14} strokeWidth={1.5} /> : <ChevronRight size={14} strokeWidth={1.5} />}
        </button>
      )}

      {/* Child count badge (when collapsed) */}
      {canExpand && !isExpanded && (
        <div
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full font-mono text-[10px] font-bold flex items-center justify-center text-white shadow-bark-sm"
          style={{ backgroundColor: color }}
        >
          {childCount}
        </div>
      )}

      {/* Source handle (bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className={`!w-3 !h-3 !border-2 !border-surface !-bottom-1.5 ${isTerminal ? '!bg-transparent !border-transparent' : ''}`}
        style={{ backgroundColor: isTerminal ? undefined : 'var(--border-strong)' }}
      />
    </div>
  );
}

export default memo(GraphNode);
